import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { useChecklistItemReceiptQuery } from './useChecklistItemReceiptQuery';
import { completedViaLabel, formatChecklistDate } from './checklistWords';

export interface ReceiptDialogProps {
  itemId: string | null;
  schoolId: string;
  familyPath: string;
  familyEnrollmentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReceiptDialog({ itemId, schoolId, familyPath, familyEnrollmentId, open, onOpenChange }: ReceiptDialogProps) {
  const { data, loading, error } = useChecklistItemReceiptQuery(schoolId, open ? itemId ?? undefined : undefined);
  const receipt = data?.checklistItemReceipt;
  const id = encodeURIComponent(familyEnrollmentId);

  const hrefFor = (kind: string, url: string | null | undefined) => {
    switch (kind) {
      case 'SIGNED_PACKET': return `${familyPath}/enrollment-packet/${id}`;
      case 'TUITION_AGREEMENT': return `${familyPath}/tuition-agreement/${id}`;
      case 'BILLING': return `${familyPath}/billing`;
      default: return url ?? undefined;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[85vh] overflow-y-auto max-w-[calc(100vw-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="break-words">
            {receipt ? `${receipt.title}${receipt.personName ? ` — ${receipt.personName}` : ''}` : 'Receipt'}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error || !receipt ? (
          <p className="text-sm text-destructive">Couldn't load this receipt.</p>
        ) : (
          <div className="min-w-0 space-y-4">
            {receipt.completedAt && (
              <p className="text-sm text-muted-foreground break-words">
                {`Completed ${formatChecklistDate(receipt.completedAt)} · ${completedViaLabel(receipt.completedVia)}`}
                {receipt.completedBy && receipt.completedBy !== 'Not recorded' ? ` · by ${receipt.completedBy}` : ''}
              </p>
            )}
            {receipt.sections.map((section, i) => (
              <div key={i} className="min-w-0 space-y-2">
                {section.heading && <h4 className="font-semibold text-sm break-words">{section.heading}</h4>}
                <dl className="space-y-1 text-sm">
                  {section.rows.map((row, j) => (
                    <div key={j} className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 min-w-0">
                      <dt className="text-muted-foreground break-words min-w-0">{row.label}</dt>
                      <dd className="sm:col-span-2 break-words min-w-0">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
            {receipt.links.length > 0 && (
              <ul className="space-y-1 text-sm">
                {receipt.links.map((link, i) => {
                  const href = hrefFor(link.kind, link.url);
                  return (
                    <li key={i} className="break-words min-w-0">
                      {link.kind === 'DOCUMENT' ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{link.label}</a>
                      ) : (
                        <Link to={href ?? '#'} className="text-primary hover:underline">{link.label}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReceiptDialog;
